import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  FileText,
  Video,
  Download,
  Search,
  Filter,
  Clock,
  Bell,
  Mail,
  ArrowRight,
  Sparkles,
  Users,
  Calendar,
  Star,
  PlayCircle,
  BookMarked,
  GraduationCap,
  Lightbulb,
  TrendingUp
} from 'lucide-react';
import { Button } from 'src/components/ui/Button';
import { Badge } from 'src/components/ui/Badge';
import { Input } from 'src/components/ui/Input';

const Resources = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleNotifyMe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setIsSubscribed(true);
      // Here you would typically send the email to your backend
      console.log('Subscribed email:', email);
    }
  };

  // Preview of upcoming resource categories
  const upcomingResources = [
    {
      category: "Study Guides",
      icon: <BookOpen className="w-6 h-6" />,
      description: "Comprehensive study materials for all subjects",
      color: "from-blue-500 to-blue-600",
      estimatedCount: "50+"
    },
    {
      category: "Video Tutorials",
      icon: <Video className="w-6 h-6" />,
      description: "Step-by-step video explanations and demonstrations",
      color: "from-purple-500 to-purple-600",
      estimatedCount: "100+"
    },
    {
      category: "Practice Tests",
      icon: <FileText className="w-6 h-6" />,
      description: "Mock exams and practice assessments",
      color: "from-green-500 to-green-600",
      estimatedCount: "30+"
    },
    {
      category: "Templates & Tools",
      icon: <Download className="w-6 h-6" />,
      description: "Downloadable templates and learning tools",
      color: "from-orange-500 to-orange-600",
      estimatedCount: "25+"
    },
    {
      category: "Research Papers",
      icon: <BookMarked className="w-6 h-6" />,
      description: "Latest research and academic publications",
      color: "from-indigo-500 to-indigo-600",
      estimatedCount: "75+"
    },
    {
      category: "Interactive Simulations",
      icon: <PlayCircle className="w-6 h-6" />,
      description: "Hands-on learning experiences and simulations",
      color: "from-pink-500 to-pink-600",
      estimatedCount: "40+"
    }
  ];

  const features = [
    {
      icon: <Search className="w-5 h-5" />,
      title: "Smart Search",
      description: "Start with a question, a topic, or a goal — then get to the material that matters."
    },
    {
      icon: <Filter className="w-5 h-5" />,
      title: "Advanced Filters",
      description: "Narrow a growing library by subject, level, format, and the way you like to learn."
    },
    {
      icon: <Download className="w-5 h-5" />,
      title: "Offline Access",
      description: "Keep selected resources close when study time happens away from a connection."
    },
    {
      icon: <TrendingUp className="w-5 h-5" />,
      title: "Progress Tracking",
      description: "See the ground you have covered and return to the next useful thing with confidence."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section */}
      <section className="relative py-20 px-4 text-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"></div>
        <div className="relative max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Coming Soon
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Learning <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Resources</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            We're building an extensive library of study materials, practice tests, video tutorials, and interactive tools 
            to supercharge your learning journey. Get ready for something amazing!
          </p>
          
          {/* Coming Soon Animation */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-32 h-32 rounded-full flex items-center justify-center">
                <img
                  src="/favicon.png"
                  alt="rfLearn Logo"
                  className="w-16 h-16 object-contain"
                />
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center animate-bounce">
                <Sparkles className="w-4 h-4 text-yellow-800" />
              </div>
            </div>
          </div>

          {/* Notify Me Form */}
          <div className="max-w-md mx-auto">
            {!isSubscribed ? (
              <form onSubmit={handleNotifyMe} className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="flex-1"
                />
                <Button type="submit" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                  <Bell className="w-4 h-4 mr-2" />
                  Notify Me
                </Button>
              </form>
            ) : (
              <div className="bg-green-100 text-green-800 px-6 py-3 rounded-lg flex items-center gap-2">
                <Star className="w-5 h-5" />
                <span>Thanks! We'll notify you when resources are available.</span>
              </div>
            )}
            <p className="text-sm text-gray-500 mt-2">
              Be the first to know when our resource library launches
            </p>
          </div>
        </div>
      </section>

      {/* What's Coming Section */}
      <section className="bg-white px-4 py-20 md:py-28">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-8 border-b border-slate-200 pb-11 lg:grid-cols-[0.8fr_1.2fr] lg:gap-24">
            <div>
              <h2 className="max-w-md text-4xl font-bold leading-[1.05] tracking-[-0.03em] text-slate-950 md:text-5xl">
                The library, at a glance.
              </h2>
            </div>
            <div className="flex flex-col justify-end">
              <p className="max-w-xl text-lg leading-relaxed text-slate-600">
                Choose the kind of help you need, then spend your energy learning — not searching for where to begin.
              </p>
            </div>
          </div>

          <div className="grid gap-x-20 md:grid-cols-2">
            {upcomingResources.map((resource) => (
              <article key={resource.category} className="group flex gap-5 border-b border-slate-200 py-9 last:border-b-0 md:[&:nth-last-child(2)]:border-b-0">
                <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${resource.color} text-white shadow-sm transition-transform duration-300 ease-out group-hover:-translate-y-1`}>
                    {resource.icon}
                </div>
                <div className="min-w-0 pt-0.5">
                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="text-xl font-bold tracking-[-0.02em] text-slate-950">{resource.category}</h3>
                    <span className="shrink-0 text-xs font-semibold text-slate-500">{resource.estimatedCount} items</span>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{resource.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Features Preview */}
      <section className="relative isolate overflow-hidden bg-[#211748] px-4 py-20 text-white md:py-28">
        <div className="pointer-events-none absolute -left-20 top-8 h-72 w-72 rounded-full bg-[#6e59a5]/45 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-[#ff7f52]/20 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-6xl gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:items-center lg:gap-24">
          <div className="max-w-xl">
            <h2 className="max-w-lg text-4xl font-bold leading-[1.05] tracking-[-0.03em] text-white md:text-5xl">
              A resource library that knows learning rarely happens in a straight line.
            </h2>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-violet-100/85">
              We’re designing the small, useful details around every guide, video, and practice set — so finding your next step feels effortless.
            </p>
            <div className="mt-8 inline-flex items-center gap-3 border-b border-[#ff7f52]/60 pb-3 text-sm font-semibold text-white">
              <Sparkles className="h-4 w-4 text-[#ff9b7b]" />
              Shaping the library now
            </div>
          </div>

          <div className="divide-y divide-white/15 border-y border-white/15">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="group grid grid-cols-[auto_1fr] gap-x-5 gap-y-3 py-6 transition-colors duration-300 first:pt-7 last:pb-7 hover:bg-white/[0.045] sm:grid-cols-[3rem_11rem_1fr] sm:items-center sm:gap-y-0 sm:px-4"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-[#ffb49e] transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:scale-105 group-hover:bg-[#ff7f52] group-hover:text-white">
                  {feature.icon}
                </div>
                <h3 className="self-center text-lg font-semibold tracking-[-0.02em] text-white">{feature.title}</h3>
                <p className="col-span-2 max-w-sm text-sm leading-6 text-violet-100/75 sm:col-span-1 sm:mt-0">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Development Timeline</h2>
            <p className="text-xl text-gray-600">Here's what we're working on and when to expect it</p>
          </div>

          <div className="space-y-8">
            {[
              {
                phase: "Phase 1",
                title: "Foundation Resources",
                description: "Basic study guides and practice materials",
                timeline: "Q2 2026",
                status: "in-progress",
                items: ["Study guides", "Practice questions", "Basic templates"]
              },
              {
                phase: "Phase 2", 
                title: "Interactive Content",
                description: "Video tutorials and interactive simulations",
                timeline: "Q3 2026",
                status: "planned",
                items: ["Video library", "Interactive demos", "Virtual labs"]
              },
              {
                phase: "Phase 3",
                title: "Advanced Features",
                description: "AI-powered recommendations and analytics",
                timeline: "Q4 2026",
                status: "planned",
                items: ["Smart search", "Progress analytics", "Personalized recommendations"]
              }
            ].map((phase, index) => (
              <div key={index} className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                    phase.status === 'in-progress' ? 'bg-blue-500' : 'bg-gray-400'
                  }`}>
                    {index + 1}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold">{phase.title}</h3>
                    <Badge variant={phase.status === 'in-progress' ? 'default' : 'secondary'}>
                      {phase.timeline}
                    </Badge>
                    {phase.status === 'in-progress' && (
                      <Badge className="bg-green-100 text-green-700">In Progress</Badge>
                    )}
                  </div>
                  <p className="text-gray-600 mb-3">{phase.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {phase.items.map((item, idx) => (
                      <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Start Learning?</h2>
          <p className="text-xl mb-8 opacity-90">
            While we're building our resource library, you can already start your learning journey with our expert tutors
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="bg-white text-blue-600 hover:bg-gray-100">
              <Link to="/register" className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5" />
                Start Learning Now
              </Link>
            </Button>
            <Button size="lg" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-600 transition-colors">
              <Link to="/how-it-works" className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5" />
                Learn How It Works
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
